import { Injectable, Injector, NgZone, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import {
  BehaviorSubject, Observable, Subject,
  catchError, filter, finalize, map, take, tap, throwError,
} from 'rxjs';
import { jwtDecode } from 'jwt-decode';

import { URLs } from '../../../../environments/environment';
import { STORAGE } from '../../constants/constants.core';
import { ApiResponse } from '../../interfaces/api-response.interface';
import { AuthData, AuthUser, LoginRequest } from '../../interfaces/auth.interface';
import { LocalStorageService } from '../localStorage/local-storage.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http            = inject(HttpClient);
  private readonly localStorageSvc = inject(LocalStorageService);
  private readonly injector        = inject(Injector);
  private readonly ngZone          = inject(NgZone);

  // Lazy para evitar dependencia circular: AuthService → Router → authGuard → AuthService
  private get router(): Router { return this.injector.get(Router); }

  private readonly _user       = signal<AuthUser | null>(null);
  readonly user       = this._user.asReadonly();

  private readonly _loggingOut = signal(false);
  readonly loggingOut = this._loggingOut.asReadonly();

  // ─── Estado de sesión ────────────────────────────────────────────────────
  readonly sessionActive$  = new BehaviorSubject<boolean>(false);
  readonly sessionWarning$ = new BehaviorSubject<boolean>(false);

  // ─── Coordinación de refresh token ───────────────────────────────────────
  refreshInProgress = false;
  readonly refreshInProgress$ = new BehaviorSubject<boolean>(false);
  readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);
  readonly refreshDone$ = new Subject<string>();

  // Cotas del aviso "¿Sigues ahí?": nunca antes de 10s ni después de 60s antes
  // de expirar, sin importar la duración configurada del token.
  private static readonly WARNING_MIN_MS = 10_000;
  private static readonly WARNING_MAX_MS = 60_000;

  private warningTimeoutId?: ReturnType<typeof setTimeout>;
  private logoutTimeoutId?: ReturnType<typeof setTimeout>;
  private warningTriggeredByChannel = false;
  private logoutTriggeredByChannel  = false;
  private _isLoggingOut             = false;
  private _lastActivityMs           = Date.now();

  /**
   * Cuánto antes de expirar se agenda el aviso "¿Sigues ahí?": ~1/3 de la vida
   * total del token (exp - iat), acotado entre WARNING_MIN_MS y WARNING_MAX_MS.
   * Proporcional para que el aviso siga funcionando igual de bien sin importar
   * cuánto dure el token (application.security.jwt.expiration en el backend)
   * -- con un valor fijo, cambiar la duración del token en el backend podía
   * romper el aviso en silencio (ej. con token de 3 min y umbral fijo de 10 min,
   * el aviso nunca llegaba a mostrarse).
   */
  get warningAdvanceMs(): number {
    const lifetime = this.getTokenLifetimeMs();
    if (lifetime <= 0) return AuthService.WARNING_MIN_MS;
    return Math.min(AuthService.WARNING_MAX_MS, Math.max(AuthService.WARNING_MIN_MS, lifetime / 3));
  }

  /**
   * Umbral de actividad: misma ventana que warningAdvanceMs -- si el usuario
   * interactuó dentro de esa ventana, se considera activo y se renueva el
   * token en silencio en vez de mostrar el aviso.
   */
  private get activityThresholdMs(): number {
    return this.warningAdvanceMs;
  }

  private readonly channel = new BroadcastChannel('auth_channel');

  // Prefijos de rutas públicas (fuera del árbol con authGuard — ver app.routes.ts).
  // El logout forzado (por 401, expiración de sesión o sync entre pestañas) no debe
  // sacar al usuario de una página a la que nunca le hizo falta sesión para entrar.
  private static readonly PUBLIC_ROUTE_PREFIXES = ['/auth', '/verificar'];

  constructor() {
    // Inicializar usuario desde storage al arrancar
    const rawUser = this.localStorageSvc.getItem(STORAGE.USER);
    if (rawUser) {
      this._user.set(JSON.parse(rawUser) as AuthUser);
    }

    // Registrar actividad del usuario para el refresh silencioso
    (['mousemove', 'keydown', 'click', 'scroll'] as const).forEach(ev =>
      document.addEventListener(ev, () => { this._lastActivityMs = Date.now(); }, { passive: true })
    );

    // Sincronización entre pestañas
    this.channel.onmessage = ({ data }) => {
      const { type, token } = data;
      this.ngZone.run(() => {
        if (type === 'refresh-start') {
          this.refreshInProgress = true;
          this.refreshInProgress$.next(true);
          this.refreshTokenSubject.next(null);
        }
        if (type === 'refresh-done') {
          this.refreshInProgress = false;
          this.sessionWarning$.next(false);
          this.refreshInProgress$.next(false);
          if (token) {
            this.localStorageSvc.addItem(STORAGE.TOKEN, token);
            this.refreshDone$.next(token);
            this.refreshTokenSubject.next(token);
            // Sin esto, esta pestaña se queda con warningTimeoutId/logoutTimeoutId
            // agendados contra el token VIEJO -- su logoutTimeoutId dispara en la
            // expiración original y hace logout() (con broadcast a todas las
            // pestañas) aunque el token ya se haya renovado en otra pestaña.
            this._scheduleTimers();
          }
        }
        if (type === 'warning') {
          this.warningTriggeredByChannel = true;
          this.sessionWarning$.next(true);
        }
        if (type === 'logout') {
          this.logoutTriggeredByChannel = true;
          this._forceClear();
        }
        if (type === 'warning-clear') {
          this.sessionWarning$.next(false);
        }
      });
    };

    // Si hay sesión activa al iniciar, programar los timers
    if (this.getToken()) {
      this.sessionActive$.next(true);
      this._scheduleTimers();
    }
  }

  // ─── Operaciones de autenticación ────────────────────────────────────────

  login(credentials: LoginRequest): Observable<ApiResponse<AuthData>> {
    return this.http.post<ApiResponse<AuthData>>(URLs.login, credentials);
  }

  handleLoginSuccess(data: AuthData): void {
    this.localStorageSvc.addItem(STORAGE.TOKEN, data.token);
    this.localStorageSvc.addItem(STORAGE.REFRESH_TOKEN, data.refreshToken);
    this.localStorageSvc.addItem(STORAGE.USER, JSON.stringify(data.user));
    this._user.set(data.user);
    this.sessionActive$.next(true);
    this.channel.postMessage({ type: 'warning-clear' });
    this._scheduleTimers();
  }

  /**
   * Completa el login iniciado por el callback de SSO (MS Office 365, ver
   * MicrosoftAuthController), que llega como /ms-auth?token=...&refreshtoken=...
   * sin el resto de los datos del usuario (a diferencia de /auth/login). Guarda
   * los tokens y pide el perfil a /auth/info para poblar el signal `user` igual
   * que handleLoginSuccess.
   */
  loginWithExternalToken(token: string, refreshToken: string): Observable<AuthUser> {
    this.localStorageSvc.addItem(STORAGE.TOKEN, token);
    this.localStorageSvc.addItem(STORAGE.REFRESH_TOKEN, refreshToken);

    return this.http.post<ApiResponse<AuthUser>>(URLs.authInfo, {}).pipe(
      tap(({ data: user }) => {
        this.localStorageSvc.addItem(STORAGE.USER, JSON.stringify(user));
        this._user.set(user);
        this.sessionActive$.next(true);
        this.channel.postMessage({ type: 'warning-clear' });
        this._scheduleTimers();
      }),
      map(({ data }) => data),
    );
  }

  logout(): void {
    if (this._isLoggingOut) return;
    this._isLoggingOut = true;
    this._loggingOut.set(true);
    this._forceClear();
    this.channel.postMessage({ type: 'logout' });
  }

  /** Verifica que exista token Y que no haya expirado */
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    return this.getRemainingMs() > 0;
  }

  // ─── Acceso a tokens ─────────────────────────────────────────────────────

  getToken(): string | null {
    const value = this.localStorageSvc.getItem(STORAGE.TOKEN);
    return value || null;
  }

  getRefreshToken(): string | null {
    const value = this.localStorageSvc.getItem(STORAGE.REFRESH_TOKEN);
    return value || null;
  }

  // ─── Refresh token ───────────────────────────────────────────────────────

  refreshAccessToken(): Observable<string> {
    if (this.refreshInProgress) {
      return this.refreshTokenSubject.pipe(
        filter((t): t is string => t !== null),
        take(1)
      );
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      return throwError(() => new Error('No refresh token available'));
    }

    this.refreshInProgress = true;
    this.refreshInProgress$.next(true);
    this.refreshTokenSubject.next(null);
    this.channel.postMessage({ type: 'refresh-start' });

    return this.http.post<{ token: string }>(URLs.refreshToken, {}, {
      headers: { Authorization: `Bearer ${refreshToken}` },
    }).pipe(
      tap(({ token }) => {
        this.localStorageSvc.addItem(STORAGE.TOKEN, token);
        this.channel.postMessage({ type: 'refresh-done', token });
        this.refreshTokenSubject.next(token);
        this.refreshDone$.next(token);
        this._scheduleTimers();
      }),
      map(({ token }) => token),
      finalize(() => {
        this.refreshInProgress = false;
        this.refreshInProgress$.next(false);
      })
    );
  }

  /**
   * Se usa desde el botón "Continuar sesión" del modal de inactividad.
   * Cancela de inmediato el `logoutTimeoutId` ya agendado (con el token viejo)
   * antes de esperar la respuesta del refresh — si no, ese timer puede disparar
   * y cerrar la sesión mientras el round-trip HTTP del refresh sigue en vuelo
   * (carrera perdida si el usuario tardó en reaccionar al modal), aunque el
   * usuario ya haya dado clic en "Continuar sesión". Si el refresh falla,
   * cierra la sesión explícitamente en vez de dejarla colgada.
   */
  continueSession(): Observable<string> {
    clearTimeout(this.logoutTimeoutId);
    return this.refreshAccessToken().pipe(
      catchError(err => {
        this.logout();
        return throwError(() => err);
      })
    );
  }

  // ─── JWT utils ───────────────────────────────────────────────────────────

  getRemainingMs(): number {
    const token = this.getToken();
    if (!token) return 0;
    try {
      const { exp } = jwtDecode<{ exp: number }>(token);
      return exp * 1000 - Date.now();
    } catch {
      return 0;
    }
  }

  /**
   * Duración total del token (exp - iat) en ms, usada para escalar warningAdvanceMs.
   * Si el token no trae iat (o viene inválido), devuelve 0 -- warningAdvanceMs cae
   * a su mínimo seguro (WARNING_MIN_MS) en vez de propagar un NaN a los timers.
   */
  private getTokenLifetimeMs(): number {
    const token = this.getToken();
    if (!token) return 0;
    try {
      const { exp, iat } = jwtDecode<{ exp: number; iat: number }>(token);
      if (!Number.isFinite(exp) || !Number.isFinite(iat)) return 0;
      return (exp - iat) * 1000;
    } catch {
      return 0;
    }
  }

  // ─── Timers de sesión ────────────────────────────────────────────────────

  private _scheduleTimers(): void {
    this._clearTimers();
    const remaining = this.getRemainingMs();
    if (remaining <= 0) { this.logout(); return; }

    this.warningTimeoutId = setTimeout(() => {
      if (this.warningTriggeredByChannel) return;

      const userIsActive = Date.now() - this._lastActivityMs < this.activityThresholdMs;
      if (userIsActive) {
        // El usuario está activo: renovar el token en silencio
        this.refreshAccessToken().subscribe({
          error: () => {
            // Si el refresh falla, mostrar el warning como fallback
            this.sessionWarning$.next(true);
            this.channel.postMessage({ type: 'warning' });
          },
        });
      } else {
        this.sessionWarning$.next(true);
        this.channel.postMessage({ type: 'warning' });
      }
    }, Math.max(0, remaining - this.warningAdvanceMs));

    this.logoutTimeoutId = setTimeout(() => {
      if (!this.logoutTriggeredByChannel) {
        this.logout();
      }
    }, remaining);
  }

  private _clearTimers(): void {
    clearTimeout(this.warningTimeoutId);
    clearTimeout(this.logoutTimeoutId);
    this.warningTriggeredByChannel = false;
    this.logoutTriggeredByChannel  = false;
    this.sessionWarning$.next(false);
  }

  private _forceClear(): void {
    this.localStorageSvc.removeItem(STORAGE.TOKEN);
    this.localStorageSvc.removeItem(STORAGE.REFRESH_TOKEN);
    this.localStorageSvc.removeItem(STORAGE.USER);
    this._clearTimers();
    this._user.set(null);
    this._loggingOut.set(false);
    this._isLoggingOut = false;
    this.sessionActive$.next(false);

    // La limpieza de storage/estado siempre corre; solo el redirect se salta en
    // rutas públicas — ahí nunca hizo falta sesión, así que sacar al usuario sería
    // incorrecto (ej. la página pública de verificación de constancias por QR).
    const enRutaPublica = AuthService.PUBLIC_ROUTE_PREFIXES.some(prefix => this.router.url.startsWith(prefix));
    if (!enRutaPublica) {
      this.router.navigate(['/auth/login']);
    }
  }
}
